// src/utils/lessonGenerator.js

// Función auxiliar para obtener distractores inteligentes
const getDistractors = (correctWord, allWords, count = 3) => {
  const otherWords = allWords.filter(w => w.en !== correctWord.en);
  
  // Priorizamos palabras de longitud similar para mayor desafío
  const sortedByLength = otherWords.sort((a, b) => {
    const diffA = Math.abs(a.en.length - correctWord.en.length);
    const diffB = Math.abs(b.en.length - correctWord.en.length);
    return diffA - diffB;
  });

  return sortedByLength
    .slice(0, count * 2)
    .sort(() => Math.random() - 0.5)
    .slice(0, count)
    .map(w => w.en);
};

// ✅ FUNCIÓN PRINCIPAL CON DISTRIBUCIÓN 40% MATCH, 30% MC, 15% INPUT, 15% ORDER
export const generatePracticeLesson = (vocabSet, failedWordsIds = []) => {
  const questions = [];
  const sourceWords = vocabSet?.words || [];
  
  // 1. Barajamos y seleccionamos máximo 12 palabras
  const allWords = [...sourceWords]
    .sort(() => Math.random() - 0.5)
    .slice(0, 16);

  // --- PASO 1: GENERAR MATCH (40% DEL PESO) ---
  // Para 12 palabras: ~5 pares = 10 palabras en Match
  if (allWords.length >= 4) {
    const matchPool = [];
    const seenTexts = new Set();
    
    // Barajamos y seleccionamos hasta 5 pares únicos
    [...allWords].sort(() => Math.random() - 0.5).forEach(word => {
      const leftText = word.en.trim().toLowerCase();
      const rightText = word.es.trim().toLowerCase();

      if (!seenTexts.has(leftText) && 
          !seenTexts.has(rightText) && 
          matchPool.length < 5) {
        
        seenTexts.add(leftText);
        seenTexts.add(rightText);
        matchPool.push(word);
      }
    });

    // Validación final: verificar que no haya duplicados
    if (matchPool.length >= 2) {
      const finalLeftTexts = matchPool.map(w => w.en.trim().toLowerCase());
      const finalRightTexts = matchPool.map(w => w.es.trim().toLowerCase());
      const allFinalTexts = [...finalLeftTexts, ...finalRightTexts];
      const hasDuplicates = allFinalTexts.length !== new Set(allFinalTexts).size;

      if (!hasDuplicates) {
        questions.push({
          type: 'match',
          q: 'Ronda Relámpago: Conecta las parejas',
          pairs: matchPool.map((w, index) => ({
            left: w.en,
            right: w.es,
            id: index
          })),
          a: 'completed',
          // ✅ SOLO SI EXISTE EN EL JSON
          ...(vocabSet.tip && { tip: vocabSet.tip })
        });
      }
    }
  }

  // --- PASO 2: GENERAR PREGUNTAS INDIVIDUALES (60% RESTANTE) ---
  // Separamos las palabras ya usadas en Match
  const usedInMatch = questions[0]?.pairs?.map(p => p.left) || [];
  const individualWords = allWords.filter(w => !usedInMatch.includes(w.en));

  // ✅ DISTRIBUCIÓN: 30% MC, 15% INPUT, 15% ORDER (del 60% restante)
  // Para ~7 palabras individuales: 3-4 MC, 1-2 INPUT, 1-2 ORDER
  
  const targetCounts = {
    mc: Math.ceil(individualWords.length * 0.5),      // ~50% del restante = 30% total
    input: Math.ceil(individualWords.length * 0.25),  // ~25% del restante = 15% total
    order: Math.floor(individualWords.length * 0.25)  // ~25% del restante = 15% total
  };

  let counts = { mc: 0, input: 0, order: 0 };

  individualWords.forEach((word, index) => {
    const isFailed = failedWordsIds.includes(word.id);
    
    // ✅ REFUERZO: Palabras fallidas SIEMPRE van a INPUT (si hay cupo)
    if (isFailed && counts.input < targetCounts.input) {
      questions.push({
        type: 'input',
        q: `Escribe en inglés: ___ (${word.es})`,
        a: word.en,
        // ✅ SOLO SI EXISTE EN EL JSON
        ...(word.tip && { tip: word.tip })
      });
      counts.input++;
      return;
    }

    // ✅ DISTRIBUCIÓN CONTROLADA por cupos
    if (counts.mc < targetCounts.mc) {
      // A) MC (30% del total)
      const distractors = getDistractors(word, allWords, 3);
      const options = [word.en, ...distractors].sort(() => Math.random() - 0.5);

      questions.push({
        type: 'mc',
        q: `¿Cómo se dice "${word.es}"?`,
        options,
        a: word.en,
        // ✅ SOLO SI EXISTE EN EL JSON
        ...(word.tip && { tip: word.tip })
      });
      counts.mc++;
    }
    else if (counts.input < targetCounts.input) {
      // B) INPUT (15% del total)
      questions.push({
        type: 'input',
        q: `Escribe en inglés: ___ (${word.es})`,
        a: word.en,
        // ✅ SOLO SI EXISTE EN EL JSON
        ...(word.tip && { tip: word.tip })
      });
      counts.input++;
    }
    else if (counts.order < targetCounts.order && word.example && word.example.trim().length > 0) {
      // C) ORDER (15% del total) - Solo si hay ejemplo
      questions.push({
        type: 'order',
        q: `Traduce: "${word.exampleEs || 'la frase'}"`,
        a: word.example,
        words: word.example.split(' ').filter(w => w.trim()),
        // ✅ SOLO SI EXISTE EN EL JSON
        ...(word.tip && { tip: word.tip })
      });
      counts.order++;
    }
    else {
      // FALLBACK: Si no hay cupo o no hay ejemplo, hacemos MC
      const distractors = getDistractors(word, allWords, 3);
      questions.push({
        type: 'mc',
        q: `¿Cómo se dice "${word.es}"?`,
        options: [word.en, ...distractors].sort(() => Math.random() - 0.5),
        a: word.en,
        // ✅ SOLO SI EXISTE EN EL JSON
        ...(word.tip && { tip: word.tip })
      });
    }
  });

  // --- PASO 3: BARAJAR PREGUNTAS (Match puede ir en cualquier posición) ---
  const matchQuestion = questions.find(q => q.type === 'match');
  const otherQuestions = questions.filter(q => q.type !== 'match');
  
  const shuffledOthers = otherQuestions.sort(() => Math.random() - 0.5);
  
  // Match va en posición aleatoria
  const matchPosition = Math.random();
  let finalQuestions = [];
  
  if (matchPosition < 0.33 && matchQuestion) {
    // Match al inicio
    finalQuestions = [matchQuestion, ...shuffledOthers];
  } else if (matchPosition < 0.66 && matchQuestion) {
    // Match en el medio
    const midPoint = Math.floor(shuffledOthers.length / 2);
    finalQuestions = [
      ...shuffledOthers.slice(0, midPoint),
      matchQuestion,
      ...shuffledOthers.slice(midPoint)
    ];
  } else {
    // Match al final o sin Match
    finalQuestions = matchQuestion 
      ? [...shuffledOthers, matchQuestion]
      : shuffledOthers;
  }

  return {
    id: `practice_${vocabSet.id || 'gen'}_${Date.now()}`,
    title: `Práctica: ${vocabSet.title || 'Vocabulario'}`,
    description: "Entrenamiento generado automáticamente",
    questions: finalQuestions
  };
};
